import { Injectable, Version } from "@angular/core";
import {
  filter,
  from,
  map,
  merge,
  Observable,
  Subject,
  Subscription,
  switchMap,
  take,
  tap,
  timer,
} from "rxjs";
import { RTCViewer } from "../../webrtc/rtc-viewer";
import { SignalingTransport } from "./signaling-transport";
import { MqttSignalingClient } from "../../webrtc/mqtt-signaling-client";
import { WebRtcSignaling } from "../models/webrtc/web-rtc-signaling";
import {
  CallSessionUpdate,
  CallSessionUpdateState,
} from "../models/webrtc/call/v2/call-session-update";
import {
  CallUpdate,
  CallUpdateState,
} from "../models/webrtc/call/v2/call-update";
import { SignalingTransportV2 } from "./signaling-transport-v2";
import {
  CallEntity,
  CallEntityKind,
  CallParticipant,
} from "../models/webrtc/call/v2/call-entity";
import { CallSignalingMessage } from "../models/webrtc/call/v2/call-signaling-message";
import { MqttSignalingClientV2 } from "../../webrtc/mqtt-signaling-client-v2";
import { IceServerConfig } from "../models/ice-server-config";
import { Call, Session } from "../models/webrtc/call/call";
import { IMqttSignalingClient } from "../../webrtc/interfaces/IMqttSignalingClient";
import { BridgeData, IotService } from "./iot.service";
import { CallApiService } from "./call-api.service";
import { AuthenticationManager } from "./authentication-manager";
import { NotificationsManagerService } from "../../notifications/services/notifications-manager.service";
import { CallSessionControlKind } from "../models/webrtc/call/v2/call-session-control-message";
import { CallDetail } from "../models/call-detail";
import { Store } from "@ngrx/store";
import { CurrentCallActions } from "../../state/call/current-call.actions";
import {
  CurrentCallState,
  CallEntry,
  CallSession,
} from "../../state/call/current-call-state";
import { selectCallsState } from "../../state/call/current-call.selector";
import { MediaStreamStore } from "./media-stream-store.service";
import { RingtoneService } from "./ringtone.service";

@Injectable()
export class CallSessionManager
  implements SignalingTransport, SignalingTransportV2
{
  private static readonly OUTGOING_CALL_TIMEOUT_MS = 45_000;
  private outgoingTimeouts = new Map<string, Subscription>();

  private subscribingCall: boolean = false;

  private callSessionStarted$ = new Subject<Session>();
  private callSessionAnswered$ = new Subject<Session>();
  private callSessionHold$ = new Subject<Session>();
  private callEnded$ = new Subject<Session>();
  private callInitError$ = new Subject<Session>();
  private callStop$ = new Subject<Session>();

  private activeSessionChanged$ = new Subject<Session>();

  private pendingJoinSubs: Map<string, Subscription> = new Map<
    string,
    Subscription
  >();

  private _answeringCallSession$ = new Subject<Session>();
  private _incomingCallEvent$ = new Subject<Call>();

  // TODO: implement
  // private entityCallUpdateSubscriptions: Map<string, Subscription> = new Map<string, Subscription>();
  // private entitySessionUpdateSubscriptions: Map<string, Subscription> = new Map<string, Subscription>();

  private pbxCallUpdateSubscription: Subscription | null = null;
  private pbxSessionUpdateSubscription: Subscription | null = null;

  private unitCallUpdateSubscription: Subscription | null = null;
  private unitSessionUpdateSubscription: Subscription | null = null;

  private incomingCallSessionAnswering: Session | null = null;

  private callsState: CurrentCallState = { calls: [], activeCallId: null };

  private calls: Call[] = [];

  /** Shared local audio stream for all viewers in current call (null if no mic available) */
  private sharedLocalAudioStream: MediaStream | null = null;
  /** Promise for acquiring the shared audio stream - resolves to null if no mic available */
  private sharedLocalAudioStreamPromise: Promise<MediaStream | null> | null = null;

  /** Shared local video stream (camera) for all viewers in current call */
  private sharedLocalVideoStream: MediaStream | null = null;
  /** Promise for acquiring the shared video stream (resolves to null if no camera available) */
  private sharedLocalVideoStreamPromise: Promise<MediaStream | null> | null = null;
  /** Whether the user has enabled camera sending */
  private cameraEnabled: boolean = false;
  /** Whether a camera device is available on this machine */
  private _cameraAvailable: boolean = false;
  /** Whether the microphone is muted (starts muted, enabled on speaking) */
  private micMuted: boolean = true;

  get isCameraEnabled(): boolean {
    return this.cameraEnabled;
  }

  get isCameraAvailable(): boolean {
    return this._cameraAvailable;
  }

  get isMicMuted(): boolean {
    return this.micMuted;
  }

  /** Helper: get a CallEntry from Redux state by callId */
  private getCallEntry(callId: string): CallEntry | null {
    return this.callsState.calls.find(c => c.callId === callId) ?? null;
  }

  /** True when pipLeg is on hold AND mainLeg exists (actively forwarding) */
  private isForwarding(callId: string): boolean {
    const entry = this.getCallEntry(callId);
    return (
      entry?.pipLeg?.status === "hold" &&
      entry?.mainLeg != null
    );
  }

  /** True when there's a pipLeg on hold (regardless of mainLeg state) */
  private hasHeldLeg(callId: string): boolean {
    const entry = this.getCallEntry(callId);
    return entry?.pipLeg?.status === "hold";
  }

  constructor(
    private iot: IotService,
    private auth: AuthenticationManager,
    private notification: NotificationsManagerService,
    private store: Store,
    private mediaStreamStore: MediaStreamStore,
    private callApi: CallApiService,
    private ringtone: RingtoneService,
  ) {
    this.store
      .select(selectCallsState)
      .subscribe((state) => {
        console.log('[CSM] Redux callsState updated:', { callCount: state.calls.length, activeCallId: state.activeCallId, calls: state.calls.map(c => ({ callId: c.callId, mainLeg: c.mainLeg?.status, pipLeg: c.pipLeg?.status })) });
        this.callsState = state;
      });

    this.listenForIncomingCallEvents();
    this.detectCameraAvailability();
  }

  /**
   * Proactively checks whether a camera device exists on this machine.
   * Also listens for device changes (e.g. USB camera plugged/unplugged).
   */
  private detectCameraAvailability(): void {
    if (!navigator.mediaDevices) {
      this._cameraAvailable = false;
      return;
    }
    const check = () => {
      navigator.mediaDevices.enumerateDevices().then((devices) => {
        this._cameraAvailable = devices.some(
          (d) => d.kind === 'videoinput' && d.deviceId !== '',
        );
      }).catch(() => {
        this._cameraAvailable = false;
      });
    };
    check();
    navigator.mediaDevices.addEventListener('devicechange', check);
  }

  /**
   * Internal: listen for incoming call events (push notifications and
   * retrieveAndNotifyIncomingCall). Routes each event based on its state:
   * - Started → enter the call session
   * - Missed / Canceled / Rejected → close the active call if it matches
   */
  private listenForIncomingCallEvents(): void {
    this.onIncomingCallEvent().subscribe((call) => {
      console.log('[CSM] listenForIncomingCallEvents received:', call.callId, 'state:', call.state, 'state===answered:', call.state === 'answered', 'state===CallUpdateState.Answered:', call.state === CallUpdateState.Answered);
      switch (call.state) {
        case CallUpdateState.Started:
        case CallUpdateState.Forwarded:
          // Ignore incoming calls whose callId is already active (e.g. outgoing
          // call to a unit the current user is also associated with)
          if (this.calls.some(c => c.callId === call.callId)) {
            console.log('[CSM] Ignoring incoming call - callId already active:', call.callId);
            break;
          }
          this.ringtone.play();
          if (call.v2) {
            console.log('[CSM] calling enterInCallSession (V2)');
            this.enterInCallSession(call);
          } else {
            console.log('[CSM] calling enterV1InCallSession (V1)');
            this.enterV1InCallSession(call);
          }
          break;

        case CallUpdateState.Missed:
        case CallUpdateState.Canceled:
        case CallUpdateState.Rejected:
          this.ringtone.stop();
          if (this.callsState.calls.some(c => c.callId === call.callId)) {
            this.closeMainLeg(call.callId);
          }
          break;
        case CallUpdateState.Answered:
          this.ringtone.stop();
          const selectedCall = this.callsState.calls.find(c => c.callId === call.callId);
          console.log('[CSM] Answered event | callId:', call.callId,
            '| found in redux:', !!selectedCall,
            '| mainLeg status:', selectedCall?.mainLeg?.status,
            '| redux callIds:', this.callsState.calls.map(c => c.callId),
            '| local callIds:', this.calls.map(c => c.callId));
          if (selectedCall && selectedCall.mainLeg?.status == 'incoming') {
            console.log('[CSM] Closing incoming call answered by someone else');
            this.closeMainLeg(call.callId);
          }
          break;
        default:
          console.log('[CSM] Unhandled call state:', call.state);
          break;
      }
    });
  }

  onIncomingCallEvent(): Observable<Call> {
    const notifications$ = this.notification.onIncomingCallNotification().pipe(
      filter((notification) => {
        // For started events: skip if call already active (prevent double-enter)
        if ((notification.state as CallUpdateState) === CallUpdateState.Started) {
          const alreadyExists = this.calls.some((c) => c.callId === notification.id);
          console.log('[CSM] notification filter:', notification.id, 'alreadyExists:', alreadyExists, 'calls:', this.calls.map(c => c.callId));
          return !alreadyExists;
        }
        // Terminal states: always let through
        return true;
      }),
      map((notification) => {
        const callEvent: Call = {
          callId: notification.id,
          installationId: notification.installation_id,
          callee: notification.callee,
          caller: notification.caller,
          source: notification.source,
          destination: notification.destination,
          entityId: notification.destination.id,
          entityKind: notification.destination.kind,
          state: notification.state as CallUpdateState,
          v2: notification.version == "v2",
        };

        return callEvent;
      }),
    );

    return merge(notifications$, this._incomingCallEvent$.asObservable());
  }

  answeringCallSession(entityId: string): Observable<Session> | null {
    if (!entityId) return null;

    return this._answeringCallSession$.asObservable().pipe(
      filter((session) => {
        const call = this.calls.find((c) =>
          c.sessions?.some((s) => s.sessionId === session.sessionId),
        );
        return (
          call !== undefined &&
          call != null &&
          call.destination?.id === entityId
        );
      }),
    );
  }

  setIncomingAnsweringCallSession(session: Session | null) {
    this.incomingCallSessionAnswering = session;
    if (session) {
      this._answeringCallSession$.next(session);
    }
  }

  getIncomingAnsweringCallSession(entityId: string) {
    if (!entityId) return null;

    const call = this.calls.find(
      (c) =>
        c.entityId === entityId &&
        c.sessions?.some(
          (s) => s.sessionId === this.incomingCallSessionAnswering?.sessionId,
        ),
    );

    return call ? this.incomingCallSessionAnswering : null;
  }

  retrieveAndNotifyIncomingCall(
    callId: string,
    installationId: string,
    destinationId: string | null,
    destinationKind: CallEntityKind | null,
  ): void {
    if (!callId || !installationId) {
      throw new Error("Invalid parameters to retrieve call.");
    }

    this.getCallDetail(callId, installationId)
      .pipe(
        tap((callDetail) => console.log('[CSM] retrieveAndNotify: callDetail state:', callDetail.state)),
        filter((callDetail) => callDetail.state === CallUpdateState.Started || callDetail.state === CallUpdateState.Forwarded),
        map((callDetail) =>
          this.mapToCall(callDetail, destinationId, destinationKind),
        ),
        tap((call) => {
          console.log('[CSM] retrieveAndNotify: emitting call', call.callId);
          this._incomingCallEvent$.next(call);
        }),
      )
      .subscribe({
        error: (err) => console.error('[CSM] retrieveAndNotify error:', err),
      });
  }

  getCaller(session: Session): CallEntity | null {
    const call = this.calls.find((c) => c.callId === session.callId);
    return call?.source ?? call?.caller ?? null;
  }

  /**
   * Start an outgoing call from caller to callee.
   *
   * @param caller - The local entity initiating the call (PBX or Unit)
   * @param callee - The remote entity being called (Doorstation, Unit, etc.)
   * @param installationId - The installation ID
   * @param existingCallId - Optional existing call ID (for incoming calls)
   */
  startCall(
    caller: CallParticipant,
    callee: CallParticipant,
    installationId: string,
    existingCallId?: string,
  ) {
    if (
      !caller?.id ||
      !caller?.kind ||
      !callee?.id ||
      !callee?.kind ||
      !installationId
    ) {
      throw new Error("Invalid parameters to start call.");
    }

    // Close any existing call before starting a new one
    this.closeCurrentCall();

    const callId = existingCallId ?? this.getCallId();
    const incoming = existingCallId != null;

    // Acquire shared local audio and video streams early (before viewers are created)
    // This ensures all viewers share the same microphone and camera streams
    // Audio acquisition is graceful - resolves to null if no mic available
    this.acquireSharedLocalAudioStream();
    if (callee.kind !== CallEntityKind.Doorstation) {
      this.acquireSharedLocalVideoStream();
    }

    // Dispatch start call action to update UI immediately
    // destination = the local entity (caller) so device-live-view can filter by entityId
    this.store.dispatch(
      CurrentCallActions.startCall({
        callId,
        installationId,
        callee,
        incoming,
        destination: { id: caller.id, kind: caller.kind },
      }),
    );

    const iceServers$ = this.getIceServers$();
    if (callee.kind === CallEntityKind.Doorstation) {
      if (callee.isV2) {
        this.startV2Call(
          callId,
          caller.id,
          caller.kind,
          callee.id,
          callee.kind,
          installationId,
          iceServers$,
        );
      } else {
        this.startV1Call(callId, installationId, callee.id, caller.id);
      }
    } else {
      this.startV2Call(
        callId,
        caller.id,
        caller.kind,
        callee.id,
        callee.kind,
        installationId,
        iceServers$,
      );
    }
  }

  forwardCall(
    callId: string,
    entityId: string,
    entityKind: CallEntityKind,
    remoteEntityId: string,
    remoteEntityKind: CallEntityKind,
    installationId: string,
  ) {
    if (
      !callId ||
      !entityId ||
      !entityKind ||
      !remoteEntityId ||
      !remoteEntityKind ||
      !installationId
    ) {
      throw new Error("Invalid parameters to forward a call.");
    }

    this.iot
      .publishForwardRequestOnCallControl$(
        callId,
        entityId,
        entityKind,
        remoteEntityId,
        remoteEntityKind,
        installationId,
      )
      .subscribe();

    const call = this.calls.find((c) => c.callId === callId);
    if (call) {
      call.state = CallUpdateState.Forwarded;
      // Create callee participant for the forward target
      const callee: CallParticipant = {
        id: remoteEntityId,
        kind: remoteEntityKind,
      };
      this.store.dispatch(CurrentCallActions.startForward({ callId: call.callId, callee }));
    }
  }

  startDeviceTalkSession(callId: string, sessionId: string) {
    this.ringtone.stop();

    if (!callId || !sessionId) {
      throw new Error("Invalid parameters to start device talk session.");
    }

    const call = this.calls.find((c) => c.callId === callId);
    if (!call) {
      return;
    }

    const session = call.sessions?.find((s) => s.sessionId === sessionId);
    if (!session) {
      return;
    }

    if (call.v2) {
      this.startV2TalkSession(call, sessionId);
      return;
    }

    this.startV1TalkSession(call, session);
  }

  sendAction(
    deviceId: string,
    entityId: string,
    installationId: string,
    address: string,
  ) {
    this.iot.publishOutputAction(deviceId, entityId, installationId, address);
  }

  /**
   * Closes the current active call (if any).
   * Used internally before starting a new call.
   */
  private closeCurrentCall(): void {
    console.log(
      "[CallSessionManager] closeCurrentCall called, calls count:",
      this.calls.length,
    );
    if (this.calls.length === 0) {
      return;
    }
    // Close the first (current) call
    const currentCall = this.calls[0];
    if (currentCall) {
      console.log(
        "[CallSessionManager] closeCurrentCall - closing call:",
        currentCall.callId,
        "state:",
        currentCall.state,
      );
      this.closeMainLeg(currentCall.callId);
    }
  }

  /**
   * Closes the mainLeg of the call.
   * If a held leg exists, keeps it and clears only mainLeg.
   * If no held leg, does full call cleanup.
   */
  closeMainLeg(callId: string): void {
    this.ringtone.stop();
    console.log("[CallSessionManager] closeMainLeg called with callId:", callId);
    if (!callId) {
      console.log("[CallSessionManager] closeMainLeg - no callId, skipping");
      return;
    }

    const call = this.calls.find((c) => c.callId === callId);
    if (!call) {
      console.log("[CallSessionManager] closeMainLeg - call not found, skipping");
      return;
    }

    const entry = this.getCallEntry(callId);
    const mainLeg = entry?.mainLeg;

    console.log("[CallSessionManager] closeMainLeg - found call:", {
      callId: call.callId,
      state: call.state,
      v2: call.v2,
      hasHeldLeg: this.hasHeldLeg(callId),
      mainLegStatus: mainLeg?.status,
      sessionsCount: call.sessions?.length,
    });

    if (!mainLeg) {
      console.log("[CallSessionManager] closeMainLeg - no main leg, nothing to do");
      return;
    }

    // Send cancel/close to the remote entity of the mainLeg
    if (call.v2) {
      const remoteEntity = mainLeg.entity;
      if (mainLeg.status === "outgoing") {
        this.iot
          .publishCancelRequestOnCallControl$(
            call.callId,
            call.entityId!,
            call.entityKind!,
            remoteEntity.id,
            remoteEntity.kind,
            call.installationId,
          )
          .subscribe();
      } else if (mainLeg.status === "speaking") {
        this.iot
          .publishCloseRequestOnCallControl$(
            call.callId,
            call.entityId!,
            call.entityKind!,
            remoteEntity.id,
            remoteEntity.kind,
            call.installationId,
          )
          .subscribe();
      }
    }

    // Stop mainLeg session viewers and clear main media streams
    this.stopMainLegSessions(call);

    if (this.hasHeldLeg(callId)) {
      // Held leg exists - clear only mainLeg, keep pipLeg on hold
      console.log("[CallSessionManager] closeMainLeg - clearing mainLeg, keeping held leg");
      this.store.dispatch(CurrentCallActions.clearMainLeg({ callId: call.callId }));
      this.mediaStreamStore.resetSecond();
    } else {
      // No held leg - full call cleanup
      console.log("[CallSessionManager] closeMainLeg - full cleanup, no held leg");
      this.clearCallSubscriptions(call);
      this.removeCall(call.callId);
      this.store.dispatch(CurrentCallActions.reset({ callId: call.callId }));
      this.mediaStreamStore.removeCallStreams(call.callId);
      this.mediaStreamStore.reset();
      this.releaseSharedLocalAudioStream();
      this.releaseSharedLocalVideoStream();
    }
  }

  /**
   * Stops all mainLeg session viewers (non-hold sessions).
   * Does NOT touch held sessions, subscriptions, or call-level state.
   */
  private stopMainLegSessions(call: Call): void {
    if (!call) {
      return;
    }

    const inflight = this.pendingJoinSubs.get(call.callId);
    if (inflight) {
      inflight.unsubscribe();
      this.pendingJoinSubs.delete(call.callId);
    }

    // if (!call.v2) {
    //   return;
    // }

    const callSessions = call.sessions?.filter((s) => s.viewer != null) ?? [];
    const sessionsToClose = callSessions.filter(
      (s) => s.status !== CallSessionUpdateState.Hold,
    );

    console.log("[CallSessionManager] stopMainLegSessions:", {
      total: callSessions.length,
      toClose: sessionsToClose.length,
    });

    for (const session of sessionsToClose) {
      session.viewer!.onCallSessionEnd = () => {
        this.callStop$.next(session);
        this.store.dispatch(
          CurrentCallActions.sessionEnded({ callId: session.callId, sessionId: session.sessionId }),
        );
      };
      session.viewer!.stop();
    }
  }

  private removeCall(callId: string) {
    console.log("Removing call:", callId);
    const callIndex = this.calls.findIndex((c) => c.callId === callId);
    if (callIndex !== -1) {
      this.calls.splice(callIndex, 1);
    }
  }

  private clearCallSubscriptions(call: Call) {
    this.clearOutgoingTimeout(call.callId);
    if (call.entityKind == CallEntityKind.Unit) {
      this.unitCallUpdateSubscription?.unsubscribe();
      this.unitSessionUpdateSubscription?.unsubscribe();
      this.unitCallUpdateSubscription = null;
      this.unitSessionUpdateSubscription = null;
    } else {
      this.pbxCallUpdateSubscription?.unsubscribe();
      this.pbxSessionUpdateSubscription?.unsubscribe();
      this.pbxCallUpdateSubscription = null;
      this.pbxSessionUpdateSubscription = null;
    }
  }

  muteMicrophone(callId: string, sessionId: string) {
    this.micMuted = true;
    const viewer = this.getViewerBy(callId, sessionId);
    viewer?.muteMicrophone();
  }

  unmuteMicrophone(callId: string, sessionId: string) {
    this.micMuted = false;
    const viewer = this.getViewerBy(callId, sessionId);
    viewer?.unmuteMicrophone();
  }

  enableCamera(callId: string, sessionId: string): void {
    if (!this._cameraAvailable) return;
    this.cameraEnabled = true;
    const viewer = this.getViewerBy(callId, sessionId);
    viewer?.enableVideoTrack();
  }

  disableCamera(callId: string, sessionId: string): void {
    this.cameraEnabled = false;
    const viewer = this.getViewerBy(callId, sessionId);
    viewer?.disableVideoTrack();
  }

  muteSessionAudio(callId: string, sessionId: string) {
    if (!callId || !sessionId) {
      throw new Error("Invalid parameters to toggleSessionAudio");
    }

    const viewer = this.getViewerBy(callId, sessionId);
    viewer?.muteAudio();
  }

  unmuteSessionAudio(callId: string, sessionId: string) {
    if (!callId || !sessionId) {
      throw new Error("Invalid parameters to toggleSessionAudio");
    }

    const viewer = this.getViewerBy(callId, sessionId);
    viewer?.unmuteAudio();
  }


  resumeAudio(callId: string): void {
    const call = this.calls.find(c => c.callId === callId);
    if (!call) return;
    for (const session of (call.sessions ?? [])) {
      session.viewer?.resumeAudio();
    }
  }

  onCallEnd = () => this.callEnded$.asObservable();

  onCallStop = () => this.callStop$.asObservable();

  onCallInitError = () => this.callInitError$.asObservable();

  getCallId = (): string => crypto.randomUUID();

  //#region Outgoing Call Timeout

  private startOutgoingTimeout(callId: string): void {
    this.clearOutgoingTimeout(callId);
    const sub = timer(CallSessionManager.OUTGOING_CALL_TIMEOUT_MS).subscribe(() => {
      const entry = this.getCallEntry(callId);
      if (entry?.mainLeg?.status === 'outgoing') {
        console.log('[CallSessionManager] Outgoing call timeout (missed):', callId);
        const call = this.calls.find(c => c.callId === callId);
        if (call?.v2 && entry.mainLeg) {
          const remoteEntity = entry.mainLeg.entity;
          this.iot.publishTimeoutRequestOnCallControl$(
            call.callId, call.entityId!, call.entityKind!,
            remoteEntity.id, remoteEntity.kind, call.installationId,
          ).subscribe();
        }
        this.closeMainLegAfterTimeout(callId);
      }
    });
    this.outgoingTimeouts.set(callId, sub);
  }

  private clearOutgoingTimeout(callId: string): void {
    const sub = this.outgoingTimeouts.get(callId);
    if (sub) {
      sub.unsubscribe();
      this.outgoingTimeouts.delete(callId);
    }
  }

  /**
   * Cleanup after timeout — same as closeMainLeg but skips sending cancel/close
   * because we already sent "timeout" (missed).
   */
  private closeMainLegAfterTimeout(callId: string): void {
    const call = this.calls.find(c => c.callId === callId);
    if (!call) return;

    this.stopMainLegSessions(call);

    if (this.hasHeldLeg(callId)) {
      this.store.dispatch(CurrentCallActions.clearMainLeg({ callId: call.callId }));
      this.mediaStreamStore.resetSecond();
    } else {
      this.clearCallSubscriptions(call);
      this.removeCall(call.callId);
      this.store.dispatch(CurrentCallActions.reset({ callId: call.callId }));
      this.mediaStreamStore.removeCallStreams(call.callId);
      this.mediaStreamStore.reset();
      this.releaseSharedLocalAudioStream();
      this.releaseSharedLocalVideoStream();
    }
  }

  //#endregion Outgoing Call Timeout

  //#region V2 Call Implementation

  private startV2Call(
    callId: string,
    entityId: string,
    entityKind: CallEntityKind,
    remoteEntityId: string,
    remoteEntityKind: CallEntityKind,
    installationId: string,
    iceServerConfig$: Observable<IceServerConfig>,
  ): void {
    if (this.subscribingCall) {
      console.error("unable to start call, another call is starting");
      return;
    }

    this.subscribingCall = true;

    const callUpdateSub = this.iot
      .subscribeToCallUpdate(entityId, entityKind)
      .subscribe((update) => {
        console.log(
          "[CallSessionManager] *** CALL UPDATE NOTIFICATION ***",
          update,
        );
        this.callUpdateMessageReceived(update);
      });

    const sessionUpdateSub = this.iot
      .subscribeToSessionUpdate(entityId, entityKind)
      .subscribe((update) => {
        console.log(
          "[CallSessionManager] *** SESSION UPDATE NOTIFICATION ***",
          update,
        );
        this.sessionUpdateMessageReceived(
          update,
          entityId,
          entityKind,
          iceServerConfig$,
        );
      });

    if (entityKind == "unit") {
      this.unitCallUpdateSubscription = callUpdateSub;
      this.unitSessionUpdateSubscription = sessionUpdateSub;
    } else {
      this.pbxCallUpdateSubscription = callUpdateSub;
      this.pbxSessionUpdateSubscription = sessionUpdateSub;
    }

    const entity: CallEntity = {
      id: entityId,
      kind: entityKind,
    };

    const remoteEntity: CallEntity = {
      id: remoteEntityId,
      kind: remoteEntityKind,
    };

    const call: Call = {
      callId,
      entityId,
      entityKind,
      caller: entity,
      callee: remoteEntity,
      remoteEntityId,
      remoteEntityKind,
      installationId,
      state: CallUpdateState.Started,
      v2: true,
      outgoing: true,
    };

    this.calls.push(call);

    // Dispatch Redux action to start call
    // this.store.dispatch(CurrentCallActions.startCall({
    //     callId,
    //     installationId,
    //     entityId,
    //     entityKind
    // }));

    this.iot
      .publishCreateRequestOnCallControl$(
        callId,
        entityId,
        entityKind,
        remoteEntityId,
        remoteEntityKind,
        installationId,
      )
      .subscribe();

    this.startOutgoingTimeout(callId);
    this.subscribingCall = false;
  }

  private startV2TalkSession(call: Call, sessionId: string): void {
    if (!call) {
      return;
    }

    const session = call.sessions?.find((s) => s.sessionId === sessionId);
    if (!session) {
      return;
    }

    session.status = CallSessionUpdateState.Answered;
    this.unmuteMicrophone(call.callId, sessionId);
    this.iot.publishOnSessionControl(
      call,
      sessionId,
      CallSessionControlKind.Answer,
    );
    this.store.dispatch(
      CurrentCallActions.sessionAnswered({ callId: call.callId, sessionId: session.sessionId }),
    );
  }

  private sessionUpdateMessageReceived(
    update: CallSessionUpdate,
    entityId: string,
    entityKind: CallEntityKind,
    iceServerConfig$: Observable<IceServerConfig>,
  ): void {
    if (!update) {
      return;
    }

    // Ignore updates for unknown calls
    const call = this.calls.find((c) => c.callId === update.call_id);
    if (call == null) {
      return;
    }

    if (
      update.state === CallSessionUpdateState.Started ||
      update.state === CallSessionUpdateState.Bridged ||
      update.state === CallSessionUpdateState.Pending
    ) {
      const signalingClient = new MqttSignalingClientV2(
        this,
        this.iot.getSessionClientId(),
        call.callId,
        update.id,
        update.installation_id,
        entityId,
        entityKind,
        update.entity.id,
        update.entity.kind,
        update.peer_id,
      );

      const session: Session = {
        callId: call.callId,
        sessionId: update.id,
        peerId: update.peer_id,
        entity: update.entity,
        status: update.state,
      };

      // Determine if this session should use main or pip slot
      const prevSession = call.sessions?.find(
        (s) => s.status === CallSessionUpdateState.Hold,
      );
      const usePipSlot = prevSession != null;

      const viewer = this.setupViewer(
        signalingClient,
        iceServerConfig$,
        session,
        usePipSlot,
      );

      session.viewer = viewer;
      this.saveCallSession(call.callId, session);
      this.callSessionStarted$.next(session);

      // Dispatch Redux action for session started
      const callSession: CallSession = {
        sessionId: session.sessionId,
        peerId: session.peerId,
        entity: session.entity,
        entityName: undefined, // Will be updated by dashboard
        entityOutputs: undefined,
        entityThingName: undefined,
        status: "started",
      };
      this.store.dispatch(
        CurrentCallActions.sessionStarted({ callId: call.callId, session: callSession }),
      );

      viewer.start();
    } else if (
      update.state === CallSessionUpdateState.Closed ||
      update.state === CallSessionUpdateState.Hold ||
      update.state === CallSessionUpdateState.Answered
    ) {
      const session = this.getSessionBy(call.callId, update.id);
      if (!session) return;

      if (update.state === CallSessionUpdateState.Closed) {
        console.log(
          "[CallSessionManager] *** SESSION CLOSED notification received ***",
          {
            callId: call.callId,
            sessionId: update.id,
            state: update.state,
            entity: update.entity,
          },
        );
      }

      session.status = update.state;

      if (update.state === CallSessionUpdateState.Answered) {
        this.clearOutgoingTimeout(call.callId);
        // Notify and close other sessions
        this.callSessionAnswered$.next(session);
        this.store.dispatch(
          CurrentCallActions.sessionAnswered({ callId: call.callId, sessionId: session.sessionId }),
        );
        this.unmuteMicrophone(call.callId, session.sessionId);
        if (call && call.sessions) {
          call.state = CallUpdateState.Answered;
          for (const otherSession of call.sessions) {
            if (
              otherSession.sessionId !== session.sessionId &&
              otherSession.status !== CallSessionUpdateState.Hold
            ) {
              if (otherSession.viewer) {
                otherSession.viewer.stop();
              }
              this.store.dispatch(
                CurrentCallActions.sessionEnded({ callId: call.callId, sessionId: otherSession.sessionId }),
              );
            }
          }
        }
      }
    } else {
      throw new Error("Invalid session update: " + update.state);
    }
  }

  private callUpdateMessageReceived(update: CallUpdate): void {
    if (!update) return;

    console.log("[CallSessionManager] callUpdateMessageReceived:", {
      id: update.id,
      state: update.state,
      caller: update.caller,
    });

    if (
      update.state == CallUpdateState.Rejected ||
      update.state == CallUpdateState.Canceled
    ) {
      console.log(
        "[CallSessionManager] Call rejected/canceled, closing call:",
        update.id,
      );
      this.clearOutgoingTimeout(update.id);
      this.closeMainLeg(update.id);
    }
  }

  publishCallSignalingMessage(
    installationId: string,
    entityId: string,
    entityKind: CallEntityKind,
    message: CallSignalingMessage,
    remotePeerId?: string,
  ): void {
    this.iot.publishCallSignalingMessage(
      installationId,
      entityId,
      entityKind,
      message,
      remotePeerId,
    );
  }

  subscribeToSessionMessage(
    installationId: string,
    entityId: string,
  ): Observable<CallSignalingMessage> {
    return this.iot.subscribeToSessionMessage(installationId, entityId);
  }

  onCallSessionStarted(): Observable<Session> {
    return this.callSessionStarted$.asObservable();
  }

  onCallSessionAnswered(): Observable<Session> {
    return this.callSessionAnswered$.asObservable();
  }

  onCallSessionHold(): Observable<Session> {
    return this.callSessionHold$.asObservable();
  }

  onActiveSessionChanged(): Observable<Session> {
    return this.activeSessionChanged$.asObservable();
  }

  enterInCallSession(incomingCall: Call): void {
    console.log('[CSM] enterInCallSession called with:', { callId: incomingCall?.callId, state: incomingCall?.state, v2: incomingCall?.v2, destination: incomingCall?.destination, caller: incomingCall?.caller, source: incomingCall?.source, callee: incomingCall?.callee });
    if (!incomingCall) {
      console.error("Invalid parameters to enter in call session.");
      return;
    }

    const entity = incomingCall.destination ?? incomingCall.callee;
    const remoteEntity = incomingCall.source ?? incomingCall.caller;
    console.log('[CSM] enterInCallSession entities:', { entity, remoteEntity });
    if (!entity || !remoteEntity) {
      console.error("[CSM] Unable to enter in call session - missing entity or remoteEntity");
      return;
    }

    // Acquire shared local audio and video streams early (before viewer is created)
    // Audio acquisition is graceful - resolves to null if no mic available
    this.acquireSharedLocalAudioStream();
    if (remoteEntity.kind !== CallEntityKind.Doorstation) {
      this.acquireSharedLocalVideoStream();
    }

    const callId = incomingCall.callId;
    const installationId = incomingCall.installationId;
    const sessionId = crypto.randomUUID().toString();
    const iceServerConfig = this.getIceServers$();

    const callUpdateSub = this.iot
      .subscribeToCallUpdate(entity!.id, entity!.kind!)
      .subscribe((update) => {
        console.log(
          "[CallSessionManager] *** CALL UPDATE NOTIFICATION ***",
          update,
        );
        this.callUpdateMessageReceived(update);
      });

    const sessionUpdateSub = this.iot
      .subscribeToSessionUpdate(entity!.id, entity!.kind)
      .subscribe((update) => {
        console.log(
          "[CallSessionManager] *** SESSION UPDATE NOTIFICATION ***",
          update,
        );
        this.sessionUpdateMessageReceived(
          update,
          entity!.id,
          entity!.kind,
          iceServerConfig,
        );
      });

    if (entity.kind == CallEntityKind.PBX) {
      this.pbxCallUpdateSubscription = callUpdateSub;
      this.pbxSessionUpdateSubscription = sessionUpdateSub;
    } else {
      this.unitCallUpdateSubscription = callUpdateSub;
      this.unitSessionUpdateSubscription = sessionUpdateSub;
    }

    const iceServers$ = this.getIceServers$();
    const signalingClient = new MqttSignalingClientV2(
      this,
      this.iot.getSessionClientId(),
      callId,
      sessionId,
      installationId,
      entity.id,
      entity.kind,
      remoteEntity.id,
      remoteEntity.kind,
    );

    const session: Session = {
      callId,
      sessionId,
      entity: remoteEntity,
    };

    // Incoming calls always use main slot initially
    const viewer: RTCViewer = this.setupViewer(
      signalingClient,
      iceServers$,
      session,
      false, // usePipSlot
      true, // answerer
    );

    session.viewer = viewer;
    this.calls.push(incomingCall);

    // Dispatch Redux action for incoming call start
    // For incoming calls, the callee is the remote entity (the one calling us)
    const calleeForRedux: CallParticipant = {
      id: remoteEntity.id,
      kind: remoteEntity.kind,
      isV2: incomingCall.v2,
    };
    console.log('[CSM] enterInCallSession dispatching startCall:', { callId, installationId, callee: calleeForRedux, incoming: true, originalCallee: incomingCall.callee, destination: entity });
    this.store.dispatch(
      CurrentCallActions.startCall({
        callId,
        installationId,
        callee: calleeForRedux,
        incoming: true,
        originalCallee: incomingCall.callee,
        destination: entity,
      }),
    );

    console.log('[CSM] enterInCallSession starting viewer for callId:', callId, 'sessionId:', sessionId);
    from(viewer.start())
      .pipe(
        tap(() => {
          console.log('[CSM] enterInCallSession viewer started, publishing SessionControl.New');
          const call = this.calls.find((c) => c.callId === callId);
          this.iot.publishOnSessionControl(
            call!,
            sessionId,
            CallSessionControlKind.New,
          );
          incomingCall.sessions = incomingCall.sessions
            ? [...incomingCall.sessions, session]
            : [session];
          this.callSessionStarted$.next(session);

          // Dispatch Redux action for session started
          const callSession: CallSession = {
            sessionId: session.sessionId,
            peerId: session.peerId,
            entity: remoteEntity,
            entityName: undefined, // Will be updated by dashboard
            entityOutputs: undefined,
            entityThingName: undefined,
            status: "started",
          };
          console.log('[CSM] enterInCallSession dispatching sessionStarted:', { callId, session: callSession });
          this.store.dispatch(
            CurrentCallActions.sessionStarted({ callId, session: callSession }),
          );
        }),
      )
      .subscribe({
        error: (err) => console.error('[CSM] enterInCallSession viewer.start() error:', err),
      });
  }

  private enterV1InCallSession(incomingCall: Call): void {
    console.log('[CSM] enterV1InCallSession called with:', { callId: incomingCall?.callId, state: incomingCall?.state, destination: incomingCall?.destination, caller: incomingCall?.caller, source: incomingCall?.source });

    const entity = incomingCall.destination ?? incomingCall.callee;
    const remoteEntity = incomingCall.source ?? incomingCall.caller;
    if (!entity || !remoteEntity) {
      console.error("[CSM] Unable to enter V1 call session - missing entity or remoteEntity");
      return;
    }

    // Acquire shared local audio stream early (graceful - resolves to null if no mic)
    this.acquireSharedLocalAudioStream();

    const callId = incomingCall.callId;
    const installationId = incomingCall.installationId;
    const deviceId = remoteEntity.id;
    const entityId = entity.id;
    const iceServers$ = this.getIceServers$();

    this.calls.push(incomingCall);

    // Dispatch Redux startCall (incoming, V1)
    this.store.dispatch(CurrentCallActions.startCall({
      callId,
      installationId,
      callee: { id: remoteEntity.id, kind: remoteEntity.kind, isV2: false },
      incoming: true,
      originalCallee: incomingCall.callee,
      destination: entity,
    }));

    // Subscribe to V1 join status MQTT topic
    const sub = this.iot
      .subscribeToJoinStatus(deviceId, installationId, entityId)
      .pipe(filter(v => v !== null), take(1))
      .subscribe((info) => {
        const sessionClientId = this.iot.getSessionClientId();
        const signalingClient = new MqttSignalingClient(
          this, info!.clientId!, installationId, entityId, deviceId, sessionClientId,
        );

        const session: Session = {
          callId, sessionId: sessionClientId,
          clientId: info!.clientId!, deviceId,
        };

        incomingCall.sessions = [session];

        const viewer = this.setupViewer(signalingClient, iceServers$, session, false);
        session.viewer = viewer;
        this.callSessionStarted$.next(session);

        // Redux sessionStarted
        this.store.dispatch(CurrentCallActions.sessionStarted({
          callId,
          session: {
            sessionId: session.sessionId,
            peerId: undefined,
            entity: { id: deviceId, kind: CallEntityKind.Doorstation },
            entityName: undefined,
            entityOutputs: undefined,
            entityThingName: undefined,
            status: 'started',
          },
        }));

        viewer.start();
      });

    sub.add(() => {
      if (this.pendingJoinSubs.get(callId) === sub)
        this.pendingJoinSubs.delete(callId);
    });
    this.pendingJoinSubs.set(callId, sub);

    // Trigger doorstation to start streaming
    this.iot.publishViewOnJoin(deviceId, installationId, entityId);
  }

  hold(callId: string): void {
    console.log("[CallSessionManager] hold() called for callId:", callId);
    const call = this.calls.find((c) => c.callId === callId);
    if (call == null) {
      console.log("[CallSessionManager] hold - Call not found", callId);
      return;
    }

    const answeredSession = call.sessions?.find(
      (s) => s.status === CallSessionUpdateState.Answered,
    );
    if (answeredSession == null) {
      console.log("[CallSessionManager] hold - No answered session found");
      return;
    }

    console.log(
      "[CallSessionManager] Putting session on hold:",
      answeredSession.sessionId,
    );
    answeredSession.status = CallSessionUpdateState.Hold;
    answeredSession.viewer?.hold();
    this.iot.publishOnSessionControl(
      call,
      answeredSession.sessionId,
      CallSessionControlKind.Hold,
    );
    this.callSessionHold$.next(answeredSession);

    // Dispatch Redux action for hold - this will move mainLeg to pipLeg
    // Note: We don't move media streams here - the CSS animation handles the visual
    // transition by animating the main video element to the pip position
    console.log(
      "[CallSessionManager] Dispatching hold action (mainLeg -> pipLeg)",
    );
    this.store.dispatch(CurrentCallActions.hold({ callId }));
  }

  resume(callId: string): void {
    console.log("[CallSessionManager] resume() called for callId:", callId);
    const call = this.calls.find((c) => c.callId == callId);
    if (call == null) {
      console.log("[CallSessionManager] resume - Call not found", callId);
      return;
    }

    const holdSession = call?.sessions?.find(
      (s) => s.status == CallSessionUpdateState.Hold,
    );
    if (holdSession == null) {
      console.log("[CallSessionManager] resume - No hold session found");
      return;
    }

    const sessionsToClose = call?.sessions?.filter(
      (s) => s.sessionId != holdSession.sessionId,
    );
    console.log(
      "[CallSessionManager] resume - Sessions to close:",
      sessionsToClose?.length,
    );
    sessionsToClose?.forEach((s) => s.viewer?.stop());

    call.state = CallUpdateState.Answered;
    holdSession.status = CallSessionUpdateState.Answered;
    holdSession.viewer?.resume();
    this.iot.publishOnSessionControl(
      call,
      holdSession.sessionId,
      CallSessionControlKind.Resume,
    );

    // Dispatch Redux action for resume - this will move pipLeg back to mainLeg
    // Note: Media streams stay in place - CSS handles the visual transition
    console.log(
      "[CallSessionManager] Dispatching resume action (pipLeg -> mainLeg)",
    );
    this.store.dispatch(CurrentCallActions.resume({ callId }));
    this.activeSessionChanged$.next(holdSession);

    // Clear pip stream slot if there was a forward call that ended
    this.mediaStreamStore.resetSecond();
  }

  canBridge(callId: string): boolean {
    const call = this.calls.find((c) => c.callId === callId);
    if (call == null) {
      return false;
    }

    const holdSession = call.sessions?.find(
      (s) => s.status === CallSessionUpdateState.Hold,
    );
    const answeredSession = call.sessions?.find(
      (s) => s.status === CallSessionUpdateState.Answered,
    );

    return (
      holdSession != null &&
      answeredSession != null &&
      holdSession.entity != null &&
      answeredSession.entity != null
    );
  }

  bridge(callId: string): void {
    const call = this.calls.find((c) => c.callId === callId);
    if (call == null) {
      return;
    }

    const holdSession = call.sessions?.find(
      (s) => s.status === CallSessionUpdateState.Hold,
    );
    const answeredSession = call.sessions?.find(
      (s) => s.status === CallSessionUpdateState.Answered,
    );

    if (holdSession == null || answeredSession == null) {
      return;
    }

    if (!holdSession.entity || !answeredSession.entity) {
      return;
    }

    const bridgeSessionId = crypto.randomUUID().toString();

    const bridgeData: BridgeData = {
      answerer: holdSession.entity,
      answererPeerId: holdSession.peerId,
      offerer: answeredSession.entity,
      offererPeerId: answeredSession.peerId,
    };

    this.iot.publishOnSessionControl(
      call,
      bridgeSessionId,
      CallSessionControlKind.Bridge,
      bridgeData,
    );

    this.store.dispatch(CurrentCallActions.bridge({ callId }));
  }

  //#endregion V2 Call Implementation

  //#region V1 Call Implementation

  private startV1Call(
    callId: string,
    installationId: string,
    deviceId: string,
    entityId: string,
  ) {
    if (this.subscribingCall) {
      console.error("unable to start V1 call, another V1 call is starting");
      return;
    }

    this.subscribingCall = true;

    const iceServers$ = this.getIceServers$();

    const call: Call = {
      callId,
      entityId,
      installationId,
      v2: false,
    };

    this.calls.push(call);
    this.startOutgoingTimeout(callId);

    const sub = this.iot
      .subscribeToJoinStatus(deviceId, installationId, entityId)
      .pipe(
        filter((v) => v !== null),
        take(1),
      )
      .subscribe((info) => {
        const sessionClientId = this.iot.getSessionClientId();
        const signalingClient = new MqttSignalingClient(
          this,
          info!.clientId!,
          installationId,
          entityId,
          deviceId,
          sessionClientId,
        );

        const session: Session = {
          callId: callId,
          sessionId: sessionClientId,
          clientId: info!.clientId!,
          deviceId,
        };

        call.sessions = [session];

        // V1 calls always use main slot
        const viewer = this.setupViewer(
          signalingClient,
          iceServers$,
          session,
          false, // usePipSlot
        );
        session.viewer = viewer;
        this.callSessionStarted$.next(session);

        // Dispatch Redux action for V1 session started
        const callSession: CallSession = {
          sessionId: session.sessionId,
          peerId: undefined,
          entity: { id: deviceId, kind: CallEntityKind.Doorstation },
          entityName: undefined,
          entityOutputs: undefined,
          entityThingName: undefined,
          status: "started",
        };
        this.store.dispatch(
          CurrentCallActions.sessionStarted({ callId: call.callId, session: callSession }),
        );

        viewer.start();
      });

    sub.add(() => {
      if (this.pendingJoinSubs.get(callId) === sub)
        this.pendingJoinSubs.delete(callId);
    });
    this.pendingJoinSubs.set(callId, sub);
    this.subscribingCall = false;
    this.iot.publishViewOnJoin(deviceId, installationId, entityId);
  }

  private startV1TalkSession(call: Call, session: Session) {
    const clientId = session.clientId;
    if (!clientId) {
      return;
    }

    this.unmuteMicrophone(call.callId, session.sessionId);
    this.iot.publishTalkRequestOnJoin(
      session.deviceId!,
      call.installationId,
      call.entityId!,
      clientId,
    );
    this.store.dispatch(
      CurrentCallActions.sessionAnswered({ callId: call.callId, sessionId: session.sessionId }),
    );
  }

  subscribeToSignalingMessage(
    deviceId: string,
    installationId: string,
    unitId: string,
    clientId: string,
  ): Observable<WebRtcSignaling> {
    return this.iot.subscribeToSignalingMessage(
      deviceId,
      installationId,
      unitId,
      clientId,
    );
  }

  sendSignalingMessage(
    deviceId: string,
    installationId: string,
    unitId: string,
    clientId: string,
    message: WebRtcSignaling,
  ): void {
    this.iot.sendSignalingMessage(
      deviceId,
      installationId,
      unitId,
      clientId,
      message,
    );
  }

  //#endregion V1 Call Implementation

  private getIceServers$(): Observable<IceServerConfig> {
    return this.callApi.get<IceServerConfig>('/turn-servers');
  }

  //#region Internal Call Session Helpers

  /**
   * Acquires the local audio stream (microphone) if not already acquired.
   * The stream is shared across all RTCViewers in the current call.
   * Uses a shared Promise to prevent multiple concurrent getUserMedia calls.
   * Resolves to null if no microphone is available (call proceeds without audio).
   */
  private acquireSharedLocalAudioStream(): Promise<MediaStream | null> {
    // Return existing stream if already acquired
    if (this.sharedLocalAudioStream) {
      return Promise.resolve(this.sharedLocalAudioStream);
    }

    // Return existing Promise if acquisition is in progress
    if (this.sharedLocalAudioStreamPromise) {
      return this.sharedLocalAudioStreamPromise;
    }

    // Start new acquisition
    this.sharedLocalAudioStreamPromise = navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        this.sharedLocalAudioStream = stream;
        // Emit to MediaStreamStore
        this.mediaStreamStore.setFirstLocalAudioStream(stream);
        return stream;
      })
      .catch((err) => {
        console.warn("Microphone not available, call will proceed without audio:", err?.name, err?.message);
        this.sharedLocalAudioStream = null;
        return null;
      });

    return this.sharedLocalAudioStreamPromise;
  }

  /**
   * Releases the shared local audio stream when call is fully terminated.
   */
  private releaseSharedLocalAudioStream(): void {
    if (this.sharedLocalAudioStream) {
      this.sharedLocalAudioStream.getTracks().forEach((t) => t.stop());
      this.sharedLocalAudioStream = null;
    }
    this.sharedLocalAudioStreamPromise = null;
    this.micMuted = true;
  }

  /**
   * Acquires the local video stream (camera) if not already acquired.
   * The stream is shared across all RTCViewers in the current call.
   * Resolves to null if no camera is available (call proceeds without local video).
   */
  private acquireSharedLocalVideoStream(): Promise<MediaStream | null> {
    if (this.sharedLocalVideoStream) {
      return Promise.resolve(this.sharedLocalVideoStream);
    }
    if (this.sharedLocalVideoStreamPromise) {
      return this.sharedLocalVideoStreamPromise;
    }
    this.sharedLocalVideoStreamPromise = navigator.mediaDevices
      .getUserMedia({ video: true })
      .then((stream) => {
        this.sharedLocalVideoStream = stream;
        this._cameraAvailable = true;
        return stream;
      })
      .catch((err) => {
        console.warn("No camera available, proceeding without local video:", err);
        this._cameraAvailable = false;
        return null;
      });
    return this.sharedLocalVideoStreamPromise;
  }

  /**
   * Releases the shared local video stream when call is fully terminated.
   */
  private releaseSharedLocalVideoStream(): void {
    if (this.sharedLocalVideoStream) {
      this.sharedLocalVideoStream.getTracks().forEach((t) => t.stop());
      this.sharedLocalVideoStream = null;
    }
    this.sharedLocalVideoStreamPromise = null;
    this.cameraEnabled = false;
  }

  private setupViewer(
    signalingClient: IMqttSignalingClient,
    iceServerConfig$: Observable<IceServerConfig>,
    session: Session,
    usePipSlot: boolean = false,
    answerer: boolean = false,
  ): RTCViewer {
    // Determine if this viewer can send video (sendrecv vs recvonly)
    // Doorstations cannot receive video, so we use recvonly for them
    const canSendVideo = session.entity?.kind !== CallEntityKind.Doorstation;

    // Pass the shared local audio and video stream Promises to the viewer
    const viewer = new RTCViewer(
      signalingClient,
      iceServerConfig$,
      answerer,
      this.sharedLocalAudioStreamPromise,
      canSendVideo,
      canSendVideo ? this.sharedLocalVideoStreamPromise : null,
    );

    // If camera was already enabled, propagate to new viewer
    if (this.cameraEnabled && canSendVideo) {
      viewer.enableVideoTrack();
    }

    // Setup stream callbacks to populate MediaStreamStore
    viewer.streamCallbacks = {
      onRemoteVideoStream: (stream) => {
        if (usePipSlot) {
          this.mediaStreamStore.setSecondVideoStream(stream);
        } else {
          this.mediaStreamStore.setFirstVideoStream(stream);
          // Per-call store only for main session (pip overwrites same callId)
          this.mediaStreamStore.setCallVideoStream(session.callId, stream);
        }
      },
      onRemoteAudioStream: (stream) => {
        if (usePipSlot) {
          this.mediaStreamStore.setSecondAudioStream(stream);
        } else {
          this.mediaStreamStore.setFirstAudioStream(stream);
          this.mediaStreamStore.setCallAudioStream(session.callId, stream);
        }
      },
      onAutoplayBlocked: () => {
        this.mediaStreamStore.setAutoplayBlocked(true);
      },
      onLocalAudioStream: (stream) => {
        // This callback is only called if viewer acquires its own stream
        // (when no shared stream was provided)
        if (!this.sharedLocalAudioStream) {
          this.sharedLocalAudioStream = stream;
        }
        if (usePipSlot) {
          this.mediaStreamStore.setSecondLocalAudioStream(stream);
        } else {
          this.mediaStreamStore.setFirstLocalAudioStream(stream);
        }
      },
    };

    viewer.onCallSessionEnd = () => {
      console.log("Session ended:", session.sessionId);

      const call = this.calls.find((c) => c.callId === session.callId);
      if (!call) {
        console.log("Call not found for ended session", session.callId);
        return;
      }

      if (session.status === CallSessionUpdateState.Started) {
        return;
      }

      const holdSession = call.sessions?.find(
        (s) => s.status === CallSessionUpdateState.Hold,
      );
      if (holdSession == null || call.sessions?.length == 1) {
        if (call.sessions?.length == 1) {
          this.store.dispatch(
            CurrentCallActions.sessionEnded({ callId: session.callId, sessionId: session.sessionId }),
          );
        }
        this.callEnded$.next(session);
      }

      // Dispatch Redux action for session ended - reducer will handle state transitions
      this.store.dispatch(
        CurrentCallActions.sessionEnded({ callId: session.callId, sessionId: session.sessionId }),
      );

      // Close call when no hold session exists or the ended session was on hold
      const shouldCloseCall =
        session.status === CallSessionUpdateState.Hold || holdSession == null;

      if (shouldCloseCall) {
        console.log("Closing call");
        // Stop and close all remaining sessions except the one that just ended
        let sessionsToClose = call.sessions?.filter(
          (s) => s.sessionId != session.sessionId,
        );
        sessionsToClose?.forEach((s) => {
          s.viewer!.stop();
          this.callStop$.next(s);
          // Dispatch sessionEnded for each closed session
          this.store.dispatch(
            CurrentCallActions.sessionEnded({ callId: call.callId, sessionId: s.sessionId }),
          );
        });

        this.removeCall(call.callId);
        this.clearCallSubscriptions(call);
        this.store.dispatch(CurrentCallActions.reset({ callId: call.callId }));
        // Release shared local streams when call is fully closed
        this.releaseSharedLocalAudioStream();
        this.releaseSharedLocalVideoStream();
        this.mediaStreamStore.removeCallStreams(call.callId);
        this.mediaStreamStore.reset();
      } else {
        // Promote the hold session to active when the ended session was not on hold
        console.log("Activating hold session:", holdSession);
        this.activeSessionChanged$.next(holdSession!);
      }
    };

    viewer.onInitializationError = () => {
      this.callInitError$.next(session);
      // Dispatch Redux action for call error
      this.store.dispatch(
        CurrentCallActions.callError({ callId: session.callId, error: "Call initialization failed" }),
      );
    };

    return viewer;
  }

  private getCallAndSession(callId: string, sessionId: string) {
    if (!callId || !sessionId) {
      throw new Error("Invalid parameters to control call session.");
    }

    const call = this.calls.find((c) => c.callId === callId);
    const session = this.getSessionBy(callId, sessionId);

    if (!call || !session) {
      throw new Error("Call or session not found.");
    }

    return { call, session };
  }

  private getSessionBy(callId: string, sessionId: string): Session | null {
    if (!callId || !sessionId) return null;

    const call = this.calls.find((c) => c.callId === callId);
    return call?.sessions?.find((s) => s.sessionId === sessionId) ?? null;
  }

  private getViewerBy(callId: string, sessionId: string): RTCViewer | null {
    return this.getSessionBy(callId, sessionId)?.viewer ?? null;
  }

  private saveCallSession(callId: string, session: Session): void {
    const call = this.calls.find((c) => c.callId === callId);

    if (!call) return;

    call.sessions ??= [];
    call.sessions.push(session);
  }

  private getCallDetail(
    callId: string,
    installationId: string,
  ) {
    return this.callApi.get<CallDetail>(`/installations/${installationId}/calls/${callId}`);
  }

  private mapToCall(
    callDetail: CallDetail,
    destinationId?: string | null,
    destinationKind?: CallEntityKind | null,
  ): Call {
    return {
      callId: callDetail.id,
      installationId: callDetail.installation_id,
      callee: callDetail.callee,
      caller: callDetail.caller.entity,
      source: callDetail.source.entity,
      entityId: callDetail.callee.id,
      entityKind: callDetail.callee.kind,
      state: callDetail.state as CallUpdateState,
      v2: true,
      ...(destinationId &&
        destinationKind && {
          destination: {
            id: destinationId,
            kind: destinationKind,
          },
        }),
    };
  }

  //#endregion Internal Call Session Helpers
}
